import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Link } from 'react-router-dom'

const contentTypeLabels: Record<string, string> = {
  'ad_group': 'Ad Group',
  'blog': 'Blog Post',
  'email': 'Email',
  'landing_page': 'Landing Page',
  'quiz': 'Quiz',
  'tool': 'Tool',
  'video': 'Video'
};

const contentTypeColors: Record<string, string> = {
  'ad_group': 'bg-blue-100 text-blue-800',
  'blog': 'bg-green-100 text-green-800',
  'email': 'bg-purple-100 text-purple-800',
  'landing_page': 'bg-orange-100 text-orange-800',
  'quiz': 'bg-pink-100 text-pink-800',
  'tool': 'bg-yellow-100 text-yellow-800',
  'video': 'bg-red-100 text-red-800'
};

const getContentTypeLabel = (type: string): string => {
  return contentTypeLabels[type] || type;
};

type Content = {
  id: string
  name: string
  content_type: string
  created_at: string
}

type ContentListProps = {
  campaignId: string
}

export function ContentList({ campaignId }: ContentListProps) {
  const [contents, setContents] = useState<Content[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchContents() {
      try {
        const { data, error } = await supabase
          .from('contents')
          .select('*')
          .eq('status', 'READY')
          .eq('campaign_id', campaignId)

        if (error) throw error
        setContents(data || [])
      } catch (error) {
        console.error('Error fetching contents:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchContents()

    const subscription = supabase
      .channel(`contents_${campaignId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'contents',
        filter: `campaign_id=eq.${campaignId}`
      }, payload => {
        if (payload.eventType === 'INSERT') {
          setContents(current => [...current, payload.new as Content])
        } else if (payload.eventType === 'DELETE') {
          setContents(current => current.filter(content => content.id !== payload.old.id))
        }
      })
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [campaignId])

  if (loading) {
    return <div>Loading contents...</div>
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8">
                No content pieces yet
              </TableCell>
            </TableRow>
          ) : (
            contents.map((content) => (
              <TableRow key={content.id}>
                <TableCell>
                  <Link
                    to={`/content/${content.id}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {content.name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Badge className={`${contentTypeColors[content.content_type]} text-xs text-center flex justify-center items-center`}>
                    {getContentTypeLabel(content.content_type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {new Date(content.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Link to={`/content/${content.id}`}>
                    <Button variant="ghost" size="sm">
                      View
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
